import { Employee } from "domain/entities/employee";
import { Permission } from "domain/entities/permission";
import { create } from "zustand";
import { persist, PersistStorage } from "zustand/middleware";
import { getSessionStorageItem, setSessionStorageItem, removeSessionStorageItem, clearSessionStorage } from "../utils/sessionStorage";
import { clearLocalStorage } from "../utils/localStorage";

type AuthState = {
  accessToken: string | null;
  refreshToken: string | null;
  userName: string | null;
  email: string | null;
  expiresAt: number;
  expiresIn: number;
  userEmployee?: Employee | undefined;
  setUserEmployee: (userEmployee: Employee) => void;
  userPermissions: Permission[] | undefined;
  setUserPermissions: (permissions: Permission[]) => void;
  setAuth: (tokenData: TokenResponse) => void;
  clearAuth: () => void;
  getPhotoUrl: () => string;
};

interface TokenResponse {
  accessToken: string;
  refreshToken: string;
  userName: string;
  email: string;
  expiresAt: number;
  expiresIn: number;
}

const createSessionStorageConfig = (): PersistStorage<AuthState> => ({
  getItem: (key) => {
    try {
      const storedValue = getSessionStorageItem(key);
      return storedValue ? JSON.parse(storedValue) : null;
    } catch {
      return null;
    }
  },
  setItem: (key, value) => {
    try {
      setSessionStorageItem(key, JSON.stringify(value));
    } catch {
      // Silently fail during build
    }
  },
  removeItem: (key) => {
    try {
      removeSessionStorageItem(key);
    } catch {
      // Silently fail during build
    }
  },
});

const isBuildTime = typeof window === 'undefined';

const createAuthStore = () => {
  const storeConfig = (set: any, get: any) => ({
    accessToken: null,
    refreshToken: null,
    userName: null,
    email: null,
    expiresAt: 0,
    expiresIn: 0,
    userEmployee: undefined,
    userPermissions: [],

    setAuth: (tokenData: TokenResponse) => {
      set({
        accessToken: tokenData.accessToken,
        refreshToken: tokenData.refreshToken,
        userName: tokenData.userName,
        email: tokenData.email,
        expiresAt: tokenData.expiresAt,
        expiresIn: tokenData.expiresIn,
      });
    },

    setUserEmployee: (userEmployee: Employee) => {
      set({ userEmployee });
    },

    setUserPermissions: (permissions: Permission[]) => {
      set({ userPermissions: permissions });
    },

    clearAuth: () => {
      clearLocalStorage();
      clearSessionStorage();
      set({
        accessToken: null,
        refreshToken: null,
        userName: null,
        email: null,
        expiresAt: 0,
        expiresIn: 0,
        userEmployee: undefined,
        userPermissions: [],
      });
    },

    getPhotoUrl: () => {
      const { userEmployee } = get();
      return userEmployee?.photo ? `${userEmployee.photo}` : "";
    },
  });

  if (isBuildTime) {
    return create<AuthState>(storeConfig);
  }

  return create(
    persist<AuthState>(storeConfig, {
      name: "auth-session-storage",
      storage: createSessionStorageConfig(),
    })
  );
};

export const useAuthStore = createAuthStore();
